import axios from 'axios';
import { API_CONFIG, AUTH_CONFIG } from '@/config';
import BannerGptService from './gpt/bannerService';

// Create axios instance with base configuration
const apiClient = axios.create({
  baseURL: API_CONFIG.BASE_URL,
  timeout: API_CONFIG.TIMEOUT,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  }
});

// Request interceptor to add auth token to all requests
apiClient.interceptors.request.use(
  config => {
    const token = localStorage.getItem(AUTH_CONFIG.TOKEN_KEY);
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
  },
  error => Promise.reject(error)
);

// Response interceptor for error handling
apiClient.interceptors.response.use(
  response => response,
  error => {
    // Handle common errors (401, 403, etc.)
    if (error.response) {
      if (error.response.status === 401) {
        // Handle unauthorized (could redirect to login)
        console.error('Unauthorized access. Please log in again.');
        // Optional: redirect to login or clear token
        // localStorage.removeItem(AUTH_CONFIG.TOKEN_KEY);
        // window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// API service object with methods for different endpoints
const apiService = {
  // Auth endpoints
  auth: {
    login(credentials) {
      return apiClient.post('/auth/login', credentials);
    },
    logout() {
      return apiClient.post('/auth/logout');
    },
    getProfile() {
      return apiClient.get('/auth/profile');
    }
  },
  
  // Courses endpoints
  courses: {
    getAll() {
      return apiClient.get('/admin/courses');
    },
    get(id) {
      return apiClient.get(`/admin/courses/${id}`);
    },
    create(courseData) {
      return apiClient.post('/admin/courses', courseData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
    },
    update(id, courseData) {
      return apiClient.put(`/admin/courses/${id}`, courseData);
    },
    delete(id) {
      return apiClient.delete(`/admin/courses/${id}`);
    },
    // Course files
    getFiles(courseId) {
      return apiClient.get(`/admin/courses/${courseId}/files`);
    },
    uploadFile(courseId, fileData) {
      return apiClient.post(`/admin/courses/${courseId}/files`, fileData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
    },
    deleteFile(courseId, fileId) {
      return apiClient.delete(`/admin/courses/${courseId}/files/${fileId}`);
    }
  },
  
  // Assessment endpoints
  assessment: {
    getQuestions() {
      return apiClient.get('/admin/assessment-questions');
    },
    getQuestion(id) {
      return apiClient.get(`/admin/assessment-questions/${id}`);
    },
    createQuestion(questionData) {
      return apiClient.post('/admin/assessment-questions', questionData);
    },
    updateQuestion(id, questionData) {
      return apiClient.put(`/admin/assessment-questions/${id}`, questionData);
    },
    deleteQuestion(id) {
      return apiClient.delete(`/admin/assessment-questions/${id}`);
    },
    
    // Assessment options
    getOptions() {
      return apiClient.get('/admin/assessment-options');
    },
    createOption(optionData) {
      return apiClient.post('/admin/assessment-options', optionData);
    },
    updateOption(id, optionData) {
      return apiClient.put(`/admin/assessment-options/${id}`, optionData);
    },
    deleteOption(id) {
      return apiClient.delete(`/admin/assessment-options/${id}`);
    },
    
    // Assessment parts
    getParts() {
      return apiClient.get('/admin/assessment-parts');
    }
  },
  
  // Blog endpoints
  blog: {
    getPosts(page = 1) {
      return apiClient.get('/admin/content/blog', { params: { page } });
    },
    getPost(id) {
      return apiClient.get(`/admin/content/blog/${id}`);
    },
    createPost(postData) {
      return apiClient.post('/admin/content/blog', postData);
    },
    updatePost(id, postData) {
      return apiClient.put(`/admin/content/blog/${id}`, postData);
    },
    deletePost(id) {
      return apiClient.delete(`/admin/content/blog/${id}`);
    }
  },
  
  // Homepage sections
  homepage: {
    getSections() {
      return apiClient.get('/admin/homepage-sections');
    },
    createSection(sectionData) {
      return apiClient.post('/admin/homepage-sections', sectionData);
    },
    updateSection(id, sectionData) {
      return apiClient.put(`/admin/homepage-sections/${id}`, sectionData);
    },
    deleteSection(id) {
      return apiClient.delete(`/admin/homepage-sections/${id}`);
    }
  },
  
  // Banner management
  banners: {
    getAll() {
      return apiClient.get('/admin/banners1');
    },
    get(id) {
      return apiClient.get(`/admin/banners1/${id}`);
    },
    create(bannerData) {
      return apiClient.post('/admin/banners1', bannerData);
    },
    update(id, bannerData) {
      return apiClient.put(`/admin/banners1/${id}`, bannerData);
    },
    delete(id) {
      return apiClient.delete(`/admin/banners1/${id}`);
    },
    toggleActive(id) {
      return apiClient.put(`/admin/banners1/${id}/toggle-active`, {});
    }
  },
  
  // GPT service
  gpt: {
    async processBannerCommand(prompt) {
      try {
        // Get the current banners for context
        const bannersResponse = await apiService.banners.getAll();
        const currentBanners = bannersResponse.data.data;
        
        // Process the command using the GPT service
        const result = await BannerGptService.processCommand(prompt, currentBanners);
        
        return { data: result };
      } catch (error) {
        console.error('GPT service error:', error);
        throw error;
      }
    }
  },
  
  // Walkthroughs endpoints
  walkthroughs: {
    getAll() {
      console.log('Calling walkthroughs API endpoint');
      return apiClient.get('/admin/walkthroughs1')
        .then(response => {
          console.log('Raw API response from walkthroughs endpoint:', response);
          return response;
        })
        .catch(error => {
          console.error('API call error in walkthroughs.getAll():', error);
          throw error;
        });
    },
    get(id) {
      return apiClient.get(`/admin/walkthroughs1/${id}`);
    },
    create(walkthroughData) {
      return apiClient.post('/admin/walkthroughs1', walkthroughData);
    },
    update(id, walkthroughData) {
      return apiClient.put(`/admin/walkthroughs1/${id}`, walkthroughData);
    },
    delete(id) {
      return apiClient.delete(`/admin/walkthroughs1/${id}`);
    }
  },
  
  // Roles and Permissions endpoints
  roles: {
    getAll() {
      return apiClient.get('/admin/roles');
    },
    getAllWithPermissions() {
      return apiClient.get('/admin/roles2/all');
    },
    get(id) {
      return apiClient.get(`/admin/roles1/${id}`);
    },
    create(roleData) {
      return apiClient.post('/admin/roles1', roleData);
    },
    update(id, roleData) {
      return apiClient.put(`/admin/roles1/${id}`, roleData);
    },
    delete(id) {
      return apiClient.delete(`/admin/roles1/${id}`);
    }
  },
  
  // Permissions endpoints
  permissions: {
    getAll() {
      return apiClient.get('/admin/permissions1');
    },
    get(id) {
      return apiClient.get(`/admin/permissions1/${id}`);
    },
    create(permissionData) {
      return apiClient.post('/admin/permissions1', permissionData);
    },
    update(id, permissionData) {
      return apiClient.put(`/admin/permissions1/${id}`, permissionData);
    },
    delete(id) {
      return apiClient.delete(`/admin/permissions1/${id}`);
    }
  },
  
  // File Upload endpoints
  uploads: {
    uploadImage(fileData, bucketName = 'breathing') {
      const formData = new FormData();
      formData.append('file', fileData);
      formData.append('bucket', bucketName);
      
      return apiClient.post('/admin/upload/image', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
    }
  },
  
  // Landing Popups endpoints
  landingPopups: {
    getAll() {
      return apiClient.get('/admin/landing-popups');
    },
    get(id) {
      return apiClient.get(`/admin/landing-popups/${id}`);
    },
    create(popupData) {
      return apiClient.post('/admin/landing-popups', popupData);
    },
    update(id, popupData) {
      return apiClient.put(`/admin/landing-popups/${id}`, popupData);
    },
    delete(id) {
      return apiClient.delete(`/admin/landing-popups/${id}`);
    },
    toggleActive(id) {
      return apiClient.put(`/admin/landing-popups/${id}/toggle-active`);
    }
  }
};

export default apiService; 